import { useMemo, useState } from 'react';

import { Account, Allocation, AllocationType, GUID, ManagementType } from '@ndvr/portfolio-api';

import { EditableCard } from '@/components/EditableCard';
import { useEditableCardGroup } from '@/components/EditableCardGroupContext';
import { Switch } from '@/components/ui/switch';
import { titleCase } from 'utils';
import {
    useAllocation,
    useListAllocations,
} from 'views/portfolio-engine/allocation-builder/allocations/allocation-queries';
import { StrategyAllocations } from 'views/portfolio-engine/allocation-builder/allocations/StrategyAllocations';
import { useUpdateAccountAllocation } from 'views/portfolio/account-queries';
import {
    AccountAllocationEditor,
    AllocationTabFilter,
} from 'views/portfolio/PortfolioPage/tabs/target-allocation/AccountAllocationEditor';
import { defaultCashEqSweep } from 'views/portfolio/PortfolioPage/tabs/target-allocation/cashEqSweepDefault';

interface AccountAllocationCardProps {
    account: Account;
    editGroup?: string;
}

const EDIT_CARD_ID = 'account-allocation';

export const AccountAllocationCard = ({ account, editGroup }: AccountAllocationCardProps) => {
    const editGroupContext = useEditableCardGroup();
    const isEditing = editGroup ? editGroupContext.isEditing(editGroup, EDIT_CARD_ID) : false;

    const { data: currentAllocation } = useAllocation(account.allocationGUID);

    const [managementType, setManagementType] = useState<ManagementType>(account.managementType);
    const [allocationGUID, setAllocationGUID] = useState<GUID<Allocation> | undefined>(account.allocationGUID);
    const [cashEqSweep, setCashEqSweep] = useState<boolean>(account.cashEqSweep);
    const defaultTab = (): AllocationTabFilter =>
        currentAllocation?.inferred ? 'INFERRED' : (currentAllocation?.allocationType ?? AllocationType.SHARED);
    const [allocationTypeFilter, setAllocationTypeFilter] = useState<AllocationTabFilter>(defaultTab);

    const { data: allocations = [], isLoading: isLoadingAllocations } = useListAllocations(isEditing);
    const filteredAllocations = useMemo(
        () =>
            allocationTypeFilter === 'INFERRED'
                ? []
                : allocations.filter((a) => a.allocationType === allocationTypeFilter),
        [allocations, allocationTypeFilter],
    );

    const { mutateAsync: updateAccountAllocation, isPending } = useUpdateAccountAllocation(
        account.accountGUID,
        account.accountGroupGUID,
    );

    const handleEdit = () => {
        setManagementType(account.managementType);
        setAllocationGUID(account.allocationGUID);
        setCashEqSweep(account.cashEqSweep);
        setAllocationTypeFilter(defaultTab());
    };

    const handleManagementTypeChange = (value: ManagementType) => {
        setManagementType(value);
        setCashEqSweep(defaultCashEqSweep(account, value));
    };

    const handleCancel = () => {
        setManagementType(account.managementType);
        setAllocationGUID(account.allocationGUID);
        setCashEqSweep(account.cashEqSweep);
    };

    const isAutomatic = managementType === ManagementType.AUTOMATIC;
    const effectiveAllocationGUID = isAutomatic ? allocationGUID : undefined;
    // The sweep flag is only editable (and only sent) for automatic accounts.
    const effectiveCashEqSweep = isAutomatic ? cashEqSweep : undefined;
    const hasChanges =
        managementType !== account.managementType ||
        effectiveAllocationGUID !== account.allocationGUID ||
        (effectiveCashEqSweep !== undefined && effectiveCashEqSweep !== account.cashEqSweep);

    const handleSave = async () => {
        try {
            await updateAccountAllocation({
                managementType,
                allocationGUID: effectiveAllocationGUID,
                cashEqSweep: effectiveCashEqSweep,
            });
        } catch {
            return; // the mutation hook already surfaced the error
        }
        if (editGroup) editGroupContext.setEditingCard(editGroup, null);
    };
    const saveDisabled = !hasChanges || (managementType === ManagementType.AUTOMATIC && !allocationGUID);
    const saveTooltipMessage = !hasChanges
        ? 'No changes to save'
        : managementType === ManagementType.AUTOMATIC && !allocationGUID
          ? 'Select an allocation'
          : '';

    return (
        <EditableCard
            headerClassName="pt-3 pb-3"
            title="Account Allocation"
            editGroup={editGroup}
            editCardId={EDIT_CARD_ID}
            onEdit={handleEdit}
            onCancel={handleCancel}
            onSave={handleSave}
            saveDisabled={saveDisabled}
            saving={isPending}
            saveTooltipMessage={saveTooltipMessage}
            hasChanges={hasChanges}
        >
            {isEditing ? (
                <AccountAllocationEditor
                    className="space-y-4"
                    managementType={managementType}
                    allocationGUID={allocationGUID}
                    onManagementTypeChange={handleManagementTypeChange}
                    onAllocationGUIDChange={setAllocationGUID}
                    allocationTypeFilter={allocationTypeFilter}
                    onAllocationTypeFilterChange={setAllocationTypeFilter}
                    filteredAllocations={filteredAllocations}
                    isLoadingAllocations={isLoadingAllocations}
                    inferredAllocation={currentAllocation?.inferred ? currentAllocation : undefined}
                    afterManagementType={
                        managementType === ManagementType.AUTOMATIC && (
                            <div className="space-y-2">
                                <label className="text-sm font-medium">CashEQ Sweep</label>
                                <div className="flex items-center space-x-2">
                                    <Switch checked={cashEqSweep} onCheckedChange={setCashEqSweep} />
                                    <span>{cashEqSweep ? 'Yes' : 'No'}</span>
                                </div>
                            </div>
                        )
                    }
                />
            ) : (
                <div className="space-y-4">
                    <div className="space-y-2">
                        <div className="text-muted-foreground text-sm font-medium">Management Type</div>
                        <div className="font-medium">{titleCase(account.managementType)}</div>
                    </div>
                    {account.managementType === ManagementType.AUTOMATIC && (
                        <div className="space-y-2">
                            <div className="text-muted-foreground text-sm font-medium">CashEQ Sweep</div>
                            <div className="font-medium">{account.cashEqSweep ? 'Yes' : 'No'}</div>
                        </div>
                    )}
                    {account.managementType === ManagementType.AUTOMATIC ? (
                        currentAllocation ? (
                            <div className="space-y-2">
                                <div className="text-muted-foreground text-sm font-medium">Allocation</div>
                                <div className="rounded-md border border-gray-300 bg-gray-50 p-3">
                                    {currentAllocation.allocationName}
                                </div>
                                <StrategyAllocations strategyAllocations={currentAllocation.strategyAllocations} />
                            </div>
                        ) : (
                            <div className="text-muted-foreground text-center">No allocation</div>
                        )
                    ) : null}
                </div>
            )}
        </EditableCard>
    );
};
